// BIGLI-59：「LINE 新需求」顯示文字與轉報價單預填（純函式，給頁面與 QuotationForm 共用、可單測）。

export const INQUIRY_STATUS_LABELS = Object.freeze({
  // BIGLI-149：不再有「處理中」
  pending: '待處理',
  quoted: '已報價',
  rejected: '已拒絕',
});

export const PARSE_STATUS_LABELS = Object.freeze({
  parsed: '已解析',
  needs_review: '待人工確認',
});

export const AVAILABILITY_LABELS = Object.freeze({
  all: '✅ 全部有貨',
  partial: '⚠️ 部分有貨',
  none: '❌ 無貨或檔期衝突',
});

export const REASON_LABELS = Object.freeze({
  ok: '可出',
  conflict: '檔期衝突／庫存不足',
  unmatched: '未對應器材',
  error: '無法比對（日期不完整）',
});

export const availabilityLabel = (inquiry) => {
  const s = inquiry?.availability?.summary;
  return s ? AVAILABILITY_LABELS[s] : '尚未比對';
};

export const itemsSummary = (items = []) =>
  items.map((it) => `${it.assetName || it.rawName} ×${it.quantity}`).join('、');

export const rentalPeriodText = (inq) => {
  if (!inq?.rentalStart) return '—';
  const s = `${inq.rentalStart}${inq.rentalStartTime ? ` ${inq.rentalStartTime}` : ''}`;
  const e = inq.rentalEnd ? `${inq.rentalEnd}${inq.rentalEndTime ? ` ${inq.rentalEndTime}` : ''}` : '';
  return e ? `${s} ～ ${e}` : s;
};

// 一鍵轉報價單：需求單 → QuotationForm 預填欄位。只帶已對應器材的品項（未對應的沒有 asset 可下單）。
// BIGLI-99：需求單門市＝取件／出貨門市，帶入報價單門市與租借門市；歸還門市照 BIGLI-98 預設同一間。
// BIGLI-107：需求單已綁客戶（後端 populate 成物件）→ 與在報價單選客戶時帶入的欄位相同；
// 未綁定維持原行為，只帶聯絡人名字與電話文字。
const boundCustomerFields = (c) => ({
  customerId: c._id,
  customerName: c.name || '',
  customerPhone: c.phone || '',
  customerEmail: c.email || '',
  contactPerson: c.contactPerson || '',
  taxId: [c.companyTitle, c.taxId].filter(Boolean).join(' / '),
});

export const buildQuotationPrefill = (inq) => ({
  store: inq.store || '',
  crossLocationPickupStore: inq.store || '',
  crossLocationReturnStore: inq.store || '',
  customerName: inq.contactName || inq.lineDisplayName || '',
  contactPerson: inq.contactName || '',
  customerPhone: inq.contactPhone || '',
  ...(inq.customer && typeof inq.customer === 'object' && inq.customer._id ? boundCustomerFields(inq.customer) : {}),
  customer: inq.customer && typeof inq.customer === 'object' && inq.customer._id ? inq.customer : null,
  pickupDate: inq.rentalStart || '',
  pickupTime: inq.rentalStartTime || '',
  returnDate: inq.rentalEnd || '',
  returnTime: inq.rentalEndTime || '',
  // BIGLI-108：上面「只帶已對應」已改：未對應器材的品項不再濾掉，以客人原文當品名、asset: null、
  // pendingAsset: true 帶入，由門市在報價單該列手動選器材（存檔前必須選好或刪掉）。
  items: (inq.items || [])
    .filter((it) => it.asset || String(it.rawName || '').trim())
    .map((it) => (it.asset
      ? { asset: String(it.asset?._id || it.asset), name: it.assetName || it.rawName, quantity: Number(it.quantity) || 1 }
      : { asset: null, name: String(it.rawName).trim(), rawName: String(it.rawName).trim(), quantity: Number(it.quantity) || 1, pendingAsset: true })),
});

// BIGLI-108：轉單後的提示。全部對應到時維持原本文案。
export const prefillToastMessage = (items = []) => {
  const pending = items.filter((it) => it.pendingAsset).length;
  if (!pending) return '已帶入 LINE 需求單內容，請確認後建立報價單';
  return `已帶入 ${items.length} 項；${pending} 項未對應器材，請手動選擇`;
};

// BIGLI-108：報價單列的「待選器材」判定（被軟刪除的列不算）。
export const isPendingAssetItem = (item) => !!item?.pendingAsset && !item.asset && !item.removed;

// BIGLI-108：存檔前檢查。rows 為畫面上的品項列（依顯示順序）；回傳 null 表示可存檔。
export const pendingAssetSaveError = (rows = []) => {
  const labels = rows
    .map((item, idx) => (isPendingAssetItem(item) ? `第 ${idx + 1} 列「${item.pendingRawName || item.name || ''}」` : null))
    .filter(Boolean);
  if (!labels.length) return null;
  return `${labels.join('、')}尚未選擇器材，請選擇器材或刪除該列後再儲存`;
};

// BIGLI-108：在「待選器材」列選好器材：單價、分類、押金等照一般品項帶入（pickedItem 為
// AssetPickerModal 交給 handleAssetSelected 的品項資料），數量與天數保留原列的值，待選標記清掉。
export const applyPickedAssetToPendingItem = (pendingItem, pickedItem) => {
  const { pendingAsset, pendingRawName, ...rest } = pendingItem;
  return {
    ...rest,
    ...pickedItem,
    id: pendingItem.id,
    quantity: pendingItem.quantity,
    days: pendingItem.days,
    daysManual: pendingItem.daysManual,
    isCustom: false,
  };
};

// BIGLI-111：轉單
export const TRANSFER_REASON_PRESETS = ['客人改到該門市取件', '該門市庫存較足'];
export const TRANSFER_PENDING_EVENT = 'line-transfer-pending-changed';

// 已轉報價單／已婉拒不能轉；回傳停用原因，可轉回空字串。
export const transferBlockedReason = (inq) => {
  if (inq.status === 'quoted' || inq.quotation) return '已轉報價單的需求單不能轉門市';
  if (inq.status === 'rejected') return '已婉拒的需求單不能轉門市';
  return '';
};

// 接手門市 LINE＠ 加好友連結＝公司設定該門市的「Line客服連結」（storeProfiles.lineService）。
// 刻意不回退公司層級連結：那可能是別家門市的帳號，貼給客人會加錯。
export const storeLineAddFriendUrl = (templates, store) => {
  const profiles = Array.isArray(templates?.storeProfiles) ? templates.storeProfiles : [];
  const p = profiles.find((x) => x && String(x.store || '').trim() === String(store || '').trim());
  return (p && String(p.lineService || '').trim()) || '';
};

// 「由 ○○ 轉入」：最後一筆轉單的原門市（只在待接手時顯示標籤）
export const lastTransfer = (inq) => {
  const list = inq.transfers || [];
  return list.length ? list[list.length - 1] : null;
};
